import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

export interface TenantListboxOption {
  value: string;
  label: string;
}

interface TenantListboxPickerProps {
  id: string;
  value: string;
  options: TenantListboxOption[];
  placeholder: string;
  onChange: (value: string) => void;
  /** Matches My Information text fields (rounded-md border, py-2.5, etc.). */
  triggerClassName?: string;
  disabled?: boolean;
}

const DEFAULT_TRIGGER =
  'flex w-full items-center justify-between rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 transition-colors hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:text-gray-100 dark:hover:border-white/20';

export function TenantListboxPicker({
  id,
  value,
  options,
  placeholder,
  onChange,
  triggerClassName = DEFAULT_TRIGGER,
  disabled = false,
}: TenantListboxPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const selectedOption = options.find((option) => option.value === value);
  const triggerLabel = selectedOption?.label ?? placeholder;

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const preferredHeight = 320;
    const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;

    if (openDown) {
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: rect.bottom + 4,
        maxHeight: Math.max(120, Math.min(preferredHeight, spaceBelow - 12)),
        overflowY: 'auto',
        zIndex: 60,
      });
      return;
    }

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + 4,
      maxHeight: Math.max(120, Math.min(preferredHeight, spaceAbove - 12)),
      overflowY: 'auto',
      zIndex: 60,
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => {
          if (!disabled) setIsOpen((open) => !open);
        }}
        className={`${triggerClassName} ${
          isOpen ? 'border-gray-500 ring-1 ring-gray-400 dark:border-white/30' : ''
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <span className={selectedOption ? '' : 'text-gray-500 dark:text-gray-400'}>{triggerLabel}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen ? (
        <ul
          ref={menuRef}
          role="listbox"
          aria-labelledby={id}
          style={menuStyle}
          className="overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-white/15 dark:bg-gray-950"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-base transition-colors ${
                    isSelected
                      ? 'bg-gray-100 font-medium text-gray-900 dark:bg-gray-800 dark:text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5'
                  }`}
                >
                  <span>{option.label}</span>
                  {isSelected ? (
                    <svg
                      className="h-4 w-4 text-gray-700 dark:text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
